import { useQuery } from '@tanstack/react-query';
import React, { FC, useContext } from 'react';
import { AngularEvents, dispatchAngularEvent } from '../angular/dispatchAngularEvent';
import { PaymentProvider } from '../components/services/AuthServiceTypes';
import { selfServiceClient } from '../components/services/selfService';
import { useReloadPageOnPaymentProviderUpdated } from './onPaymentProviderUpdated';
import { sessionRefreshTime } from './sessionRefreshTime';

const context = React.createContext<{
  paymentProvider?: PaymentProvider;
  currencyOptions: Intl.NumberFormatOptions;
  isLoading: boolean;
  isError: boolean;
}>({
  isLoading: false,
  isError: false,
  currencyOptions: {},
});

export const PaymentProviderContext: FC<{
  paymentProvider?: PaymentProvider;
}> = React.memo(({ paymentProvider, ...props }) => {
  useReloadPageOnPaymentProviderUpdated();

  const enabled = !paymentProvider;
  const { data, isLoading, isError } = useQuery(
    ['paymentProvider'],
    () => selfServiceClient.getPaymentProvider().then((p) => p || null),
    {
      keepPreviousData: false,
      enabled,
      initialData: paymentProvider,
      refetchInterval: sessionRefreshTime,
      onSuccess: (result) =>
        result && dispatchAngularEvent(AngularEvents.PaymentProviderLoaded, result),
    },
  );

  return (
    <context.Provider
      value={{
        paymentProvider: data || undefined,
        isLoading,
        isError,
        currencyOptions: { currency: data?.currency },
      }}
      {...props}
    />
  );
});

export const usePaymentProviderContext = () => useContext(context);
